import { TreeNavigatorLogic } from '../../../../tree/navigation/TreeNavigatorLogic';
import { createBrowserLogger } from '../../../../utils/createBrowserLogger';
import { Widget, WidgetOrRoot, WidgetType } from '../../../widgets/Widget';
import { WidgetRepository } from '../../../widgets/repository/WidgetRepository';
import { ContainerOrRootWidget } from '../../../widgets/repository/containers/ContainerOrRootWidget';
import { hasChildren } from '../../../widgets/repository/containers/hasChildren';
import {
  IRootWidget,
  isRoot,
} from '../../../widgets/repository/containers/root/IRootWidget';
import { isInputWidget } from '../../../widgets/repository/inputs/isInputWidget';
import {
  isFieldDatatypeEmpty,
  isFieldDatatypeHidden,
} from '../../datatype/Datatype';
import { IReference } from '../IReference';
import { IReferencePath, ReferencePathDirection } from '../IReferencePath';
import { createMaterializedTree } from './createMaterializedTree';

const logger = createBrowserLogger({
  isActive:
    process.env.DEBUG_ENGINE_REFS === 'true' &&
    process.env.NODE_ENV === 'development',
});

function getNodeId(node: WidgetOrRoot | null | undefined) {
  if (!node) return 'N.A.';
  if (isRoot(node)) return 'ROOT';
  return node.id ?? 'N.A.';
}

// TODO Introdurre una memoizzazione per evitare di ricalcolare la struttura dell'albero ad ogni chiamata.
// TODO Sostituire il forEach con una versione iterativa al posto di quella ricorsiva.
const getTreeParentsMap = (root: IRootWidget) => {
  const parentsMap = new Map<string, ContainerOrRootWidget>();

  TreeNavigatorLogic.forEachNode(root, (node, index, parent) => {
    logger.log(`[getTreeParentsMap] node: ${node.id} (c=${String((node as any).code)}), parent: ${getNodeId(parent)}`); // prettier-ignore

    if (parent) {
      parentsMap.set(node.id, parent);
    }
  });

  return parentsMap;
};

/**
 * Ottiene l'insieme dei riferimenti disponibili nell'albero a partire da un
 * nodo singolo.
 */
export function resolveTreeReferences(
  root: IRootWidget,
  originalNode: Widget,
  options?: {
    excludeSelf?: boolean;
  },
): IReference[] {
  return resolveTreeReferencesAndPath(root, originalNode, options).references;
}

/**
 * Ottiene le informazioni relative al Tree del widget corrente, come:
 * 1. La sua path relativa alla root (quanti passi devo fare verso l'alto
 *    per arrivare alla root?)
 * 2. L'elenco di Riferimenti che dalla sua posizione può "vedere", e in che
 *    "direzione" (sono figli? sono nodi ancestor?)
 */
export function resolveTreeReferencesAndPath(
  root: IRootWidget,
  originalNode: Widget,
  options?: {
    excludeSelf?: boolean;
  },
): { path: IReferencePath; references: IReference[] } {
  // Gestione del caso limite
  if (!originalNode) return { path: [], references: [] };

  const references: IReference[] = [];

  const materializedRoot = createMaterializedTree(root);
  const parentsMap = getTreeParentsMap(materializedRoot);
  const node = TreeNavigatorLogic.byId(materializedRoot, originalNode.id);

  if (!node) {
    logger.log(`[resolveTreeReferences] Node not found [${originalNode.id}]`, { originalNode, root, materializedRoot }); // prettier-ignore
    return { path: [], references: [] };
  }

  let current = node;
  let parent: ContainerOrRootWidget | undefined;
  const visited = new Set<WidgetOrRoot>();
  const path: IReferencePath = [];
  if (options?.excludeSelf) {
    visited.add(node);
    // Downward references, solamente se il nodo non è di tipo valoriale.
    // Ad esempio, se il nodo è una lista, non possiamo navigare verso il basso.
    if (hasChildren(node) && !isInputWidget(node)) {
      for (const child of node.children) {
        references.push(...resolveNodeDownwardReferences(path, child));
      }
    }
  }

  logger.log(`[resolveTreeReferences] node: ${node.id}, parent: ${getNodeId(parentsMap.get(current.id))}`); // prettier-ignore

  // Upward references
  while ((parent = parentsMap.get(current.id)) != null) {
    logger.log(`- [parent] ${current.id} (c=${String((current as any).code)},parent=${getNodeId(parent)},ptype=${parent.type})`); // prettier-ignore
    visited.add(parent);

    for (const child of parent.children) {
      if (visited.has(child)) continue;
      visited.add(child);
      references.push(...resolveNodeDownwardReferences([...path], child));
    }

    // La root termina la catena dei riferimenti.
    if (parent.type === WidgetType.Root) break;

    const parentType = WidgetRepository.find(parent.type);

    // Se è di tipo valoriale, lo segniamo per la navigazione.
    if (isInputWidget(parent)) {
      path.push({
        direction: ReferencePathDirection.Parent,
        code: parent.code,
      });
    }

    // Se il parent ha materializzato dei children, potrebbero esserci dei
    // riferimenti "virtuali" per comodità di configurazione, ad esempio
    // nelle tabelle permettiamo di riferirci alle colonne con il nome.
    const virtualReferences = parentType?.getParentVirtualReferences?.(
      path,
      parent,
    );
    if (virtualReferences) {
      references.push(...virtualReferences);
    }

    // Anche se stiamo risalendo l'albero, se il nodo ha un riferimento es.
    // una lista, lo aggiungiamo, perché il figlio deve sapere che "esiste"
    // il padre.
    // /!\ Va fatto **dopo** la modifica del path per evitare che il riferimento
    // sia inteso come "sibling" del nodo corrente.
    const parentReference = parentType?.getReference?.(path, parent);
    if (parentReference) {
      references.push(parentReference);
    }

    current = parent;
  }

  logger.log(`[resolveTreeReferences] references: ${references.length}`, references); // prettier-ignore
  return { path, references };
}

function resolveNodeDownwardReferences(
  path: IReferencePath,
  node: Widget,
): IReference[] {
  if (!node) return [];

  const widgetType = WidgetRepository.find(node.type);
  const nodeReference = widgetType?.getReference?.(path, node);

  // In caso di un riferimento gestito direttamente dalla funzione del widget,
  // non è necessario "scendere" nell'albero dei figli.
  // In caso di empty datatype, utilizzato per la gestione delle reference di
  // campi che non hanno un vero valore in sé, dobbiamo continuare a "scendere"
  // (e.g. per le subsection)
  if (nodeReference && !isFieldDatatypeEmpty(nodeReference.field)) {
    // _Solo_ se stiamo navigando verso il basso, aggiungiamo i riferimenti
    // "fratelli", es le opzioni di una multi-select.
    const siblingReferences =
      widgetType?.getSiblingReferences?.(path, node) ?? [];

    // Se il campo è hidden, aggiungiamo solo i sibling e non lui, dato che la reference non è visibile (e.g. pulsante ws-esterno).
    if (isFieldDatatypeHidden(nodeReference.field)) {
      return siblingReferences;
    }

    return [nodeReference, ...siblingReferences];
  }

  const nextPath = isInputWidget(node)
    ? [...path, { direction: ReferencePathDirection.Child, code: node.code }]
    : path;

  const result = resolveChildrenReferences(nextPath, node);

  // Se la reference esite ed è "Empty" va comunque aggiunta alle references risultanti.
  // dato che è un riferimento che non ha un valore in sé, e non viene aggiunta
  // nell'early return precedente.
  if (nodeReference && isFieldDatatypeEmpty(nodeReference?.field)) {
    result.push(nodeReference);
  }

  return result;
}

export function resolveChildrenReferences(
  path: IReferencePath,
  node: WidgetOrRoot,
): IReference[] {
  const references: IReference[] = [];

  if (hasChildren(node)) {
    if (!node.children) return references;
    for (const child of node.children) {
      references.push(...resolveNodeDownwardReferences([...path], child));
    }
  }

  return references;
}

export function resolveOtherRootReferences(
  id: string,
  root: IRootWidget,
  path: IReferencePath,
) {
  // Necessario, altrimenti delle altre Root avremmo solamente riferimenti
  // concreti (e non i materializzati, come i campi nelle tabelle)
  const materializedRoot = createMaterializedTree(root);

  return resolveChildrenReferences(
    [...path, { direction: ReferencePathDirection.Root, code: id }],
    materializedRoot,
  );
}
